import { KNOCKOUT_ROUNDS, GROUP_STAGE_ROUND_ID } from "./constants";
import type { Event, Group, EventEntry } from "./types";

const toGroupEntry = (event: Event): EventEntry => ({
  id: event.eventId,
  url: event.url ?? `/event/${event.eventId}`,
  venueTimeZoneOffset: Number(event.eventMetadata.common.venueTimeZoneOffset),
  homeTeamCountryCode:
    event.eventMetadata.metadataForAllCategoriesInEvent[event.eventMetadata.sports.homeTeamId]?.nationalTeamProfile
      .countryCode ?? "",
  homeTeamCountryAbr:
    event.eventMetadata.metadataForAllCategoriesInEvent[event.eventMetadata.sports.homeTeamId]?.nationalTeamProfile
      .alias ?? "",
  awayTeamCountryCode:
    event.eventMetadata.metadataForAllCategoriesInEvent[event.eventMetadata.sports.awayTeamId]?.nationalTeamProfile
      .countryCode ?? "",
  awayTeamCountryAbr:
    event.eventMetadata.metadataForAllCategoriesInEvent[event.eventMetadata.sports.awayTeamId]?.nationalTeamProfile
      .alias ?? "",
  matchNumber: String(event.eventMetadata.sports.tournamentEventMatchMetadata.matchNumber),
  startTimeMillis: event.eventMetadata.common.eventStartDateTime,
  group: event.eventMetadata.sports.tournamentEventMatchMetadata.group,
  venueName: event.venueName ?? "",
  venueLocation: event.formattedVenueLocation ?? "",
});

export const getNextTwoMatchesPerGroup = (
  groups: Event[][],
  categoryUrl: string | undefined,
  nowMillis: number,
): Group[] => {
  return groups.map((group) => {
    const groupLetter = group[0]?.eventMetadata.sports.tournamentEventMatchMetadata.group ?? "A";
    const groupId = `world-cup-2026-group-${groupLetter}`;
    const mostRecentTwoEvents = group
      .filter((event) => event.eventMetadata.common.eventStartDateTime > nowMillis)
      .toSorted((a, b) => a.eventMetadata.common.eventStartDateTime - b.eventMetadata.common.eventStartDateTime)
      .slice(0, 2);

    const countryCodes: string[] = [];
    const seen = new Set<string>();
    for (const event of group) {
      const metadata = event.eventMetadata.metadataForAllCategoriesInEvent;
      const homeCode = metadata[event.eventMetadata.sports.homeTeamId]?.nationalTeamProfile?.countryCode;
      const awayCode = metadata[event.eventMetadata.sports.awayTeamId]?.nationalTeamProfile?.countryCode;
      for (const code of [homeCode, awayCode]) {
        if (!code || seen.has(code)) {
          continue;
        }

        seen.add(code);
        countryCodes.push(code);
      }
    }

    let url = "";
    if (categoryUrl) {
      url = `${categoryUrl}?round=${GROUP_STAGE_ROUND_ID}`;
      if (countryCodes.length > 0) {
        url += `&tc=${countryCodes.join("%2C")}`;
      }
    }

    return {
      id: groupId,
      groupLetter,
      url,
      items: mostRecentTwoEvents.map((event) => toGroupEntry(event)),
    };
  });
};

/**
 * Buckets all non-group-stage events by their knockout round ID and returns
 * a Group card per round with the next 2 upcoming matches, ordered by
 * ascending round ID (Round of 32 → Round of 16 → … → Final).
 */
export const getKnockoutRoundCards = (
  allEvents: Event[],
  categoryUrl: string | undefined,
  nowMillis: number,
): Group[] => {
  const knockoutRoundIds = new Set<number>(KNOCKOUT_ROUNDS.map((r) => r.id));

  // Bucket events by knockout round ID
  const buckets = new Map<number, Event[]>();
  for (const event of allEvents) {
    const rounds = event.eventMetadata?.sports?.tournamentRounds;
    if (!rounds) continue;
    for (const roundId of rounds) {
      if (roundId === GROUP_STAGE_ROUND_ID || !knockoutRoundIds.has(roundId)) continue;
      if (!buckets.has(roundId)) buckets.set(roundId, []);
      buckets.get(roundId)!.push(event);
    }
  }

  // Build a Group card per knockout round in KNOCKOUT_ROUNDS order
  return KNOCKOUT_ROUNDS.filter((round) => buckets.has(round.id))
    .map((round) => {
      const events = buckets.get(round.id)!;
      const upcoming = events
        .filter((e) => e.eventMetadata.common.eventStartDateTime > nowMillis)
        .toSorted((a, b) => a.eventMetadata.common.eventStartDateTime - b.eventMetadata.common.eventStartDateTime)
        .slice(0, 2);

      const viewAllUrl = categoryUrl ? `${categoryUrl}?round=${round.id}` : undefined;

      return {
        id: `world-cup-2026-round-${round.id}`,
        groupLetter: round.label,
        url: viewAllUrl ?? "",
        items: upcoming.map((event) => toGroupEntry(event)),
      };
    })
    .filter((group) => group.items.length > 0);
};
